import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "acd_admin_token";

const getToken = () => localStorage.getItem(TOKEN_KEY);

// Module-level axios instance with interceptors. Stable reference across renders.
const authAxios = axios.create({ baseURL: API });
authAxios.interceptors.request.use((config) => {
    const t = getToken();
    if (t) config.headers.Authorization = `Bearer ${t}`;
    return config;
});

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // null = checking, false = logged out
    const [loading, setLoading] = useState(true);

    // On mount: validate any existing token
    useEffect(() => {
        const t = getToken();
        if (!t) {
            setUser(false);
            setLoading(false);
            return;
        }
        authAxios
            .get("/auth/me")
            .then((r) => setUser(r.data))
            .catch(() => {
                localStorage.removeItem(TOKEN_KEY);
                setUser(false);
            })
            .finally(() => setLoading(false));
    }, []);

    const login = useCallback(async (email, password) => {
        const { data } = await axios.post(`${API}/auth/login`, {
            email,
            password,
        });
        localStorage.setItem(TOKEN_KEY, data.access_token);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
    }, []);

    // Wire the 401 interceptor once, keyed on `logout` (stable).
    useEffect(() => {
        const id = authAxios.interceptors.response.use(
            (r) => r,
            (err) => {
                if (err.response?.status === 401) logout();
                return Promise.reject(err);
            },
        );
        return () => authAxios.interceptors.response.eject(id);
    }, [logout]);

    const value = useMemo(
        () => ({ user, loading, login, logout, authAxios }),
        [user, loading, login, logout],
    );

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
